import { Suspense, lazy } from 'react';
import { useQuery } from '@tanstack/react-query';
import { IQ_ANALYSIS_ARTIFACT_KEY_PREFIX } from '@inference.engine/widgets/web';
import { Spinner } from '@monksflow/monks-ui';
import { analysisQuery } from '@/entities/analysis';
import { ArtifactPreviewFallback } from './ArtifactPreviewFallback';

// The report body pulls in all seven section trees. Load it only once a row of
// this kind is actually opened, so it stays out of the Library's own bundle.
const IqAnalysisReportBody = lazy(() =>
  import('@/pages/AnalysisResult').then((module) => ({ default: module.IqAnalysisReportBody })),
);

interface Props {
  artifactKey: string;
}

const PreviewSpinner = () => (
  <div className="flex flex-1 items-center justify-center p-8">
    <Spinner />
  </div>
);

/**
 * Library preview for a saved IQ analysis. The artifact stores metadata only, so
 * the report is read live from the analysis id encoded in the artifact key — same
 * strategy as `FocusGroupArtifactPreview`. An analysis deleted after being saved
 * therefore renders the fallback rather than a stale snapshot.
 */
export function IqAnalysisArtifactPreview({ artifactKey }: Props) {
  const analysisId = artifactKey.startsWith(IQ_ANALYSIS_ARTIFACT_KEY_PREFIX)
    ? artifactKey.slice(IQ_ANALYSIS_ARTIFACT_KEY_PREFIX.length)
    : null;

  // The modal owns the not-found UX here, so a 404 must not also flash the
  // global toast.
  const query = useQuery(
    analysisQuery(analysisId ?? undefined, { suppressErrorNotification: true }),
  );

  if (!analysisId) return <ArtifactPreviewFallback />;

  if (query.isPending) return <PreviewSpinner />;

  if (query.isError) return <ArtifactPreviewFallback />;

  return (
    <div className="min-h-0 flex-1 overflow-auto p-6">
      <Suspense fallback={<PreviewSpinner />}>
        <IqAnalysisReportBody analysis={query.data} />
      </Suspense>
    </div>
  );
}
